import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const {
  FiFileText,
  FiCheck,
  FiX,
  FiEye,
  FiSearch,
  FiFilter,
  FiDownload,
  FiMail,
  FiPhone,
  FiMapPin,
  FiDollarSign,
  FiClock,
  FiUser,
  FiCalendar,
  FiRefreshCw,
  FiCheckCircle,
  FiTrash2
} = FiIcons;

const VerifyApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('submission_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filters and search
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Build application model from available_orders/operator_orders

  useEffect(() => {
    fetchApplications();
    
    // Set up realtime subscription for new applications
    const channel = supabase
      .channel('verify-applications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'available_orders_a7b5c9'
        },
        (payload) => {
          console.log('Realtime update received:', payload);
          // Refresh applications when changes occur
          fetchApplications();
          
          // Show toast for new submissions (when operator completes and adds submitted_pdf_url)
          if (payload.eventType === 'UPDATE' && 
              payload.new?.submitted_pdf_url && 
              !payload.old?.submitted_pdf_url) {
            toast.success('New application submitted for verification!');
          }
        }
      )
      .subscribe();
    
    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    filterAndSortApplications();
  }, [applications, statusFilter, searchQuery, sortField, sortDirection]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      console.log('Fetching applications from Supabase...');
      
      // Source of truth: available orders (applications submitted by customers)
      const { data: avail, error: availErr } = await supabase
        .from('available_orders_a7b5c9')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (availErr) {
        console.error('Error fetching available orders:', availErr);
        throw availErr;
      }
      
      console.log('Fetched available orders:', avail);
      console.log('Total orders fetched:', avail?.length || 0);

      // Enrich with operator_orders status and submission details
      const applicationsBuilt = (avail || []).map((row) => {
        // Use submitted_at if available, otherwise use updated_at or created_at
        const submissionTime = row.submitted_at || row.updated_at || row.created_at;
        const isNewSubmission = submissionTime && 
          (new Date() - new Date(submissionTime)) < 5 * 60 * 1000; // Last 5 minutes
        
        // Determine display status
        let displayStatus = 'Pending';
        // if (row.status === 'available') displayStatus = 'Pending';
        // If assigned AND has submitted_pdf_url, it means operator completed and ready for admin review
        if (row.status === 'assigned' && row.submitted_pdf_url) displayStatus = 'Processing';
        // else if (row.status === 'assigned') displayStatus = 'Filed';
        else if (row.status === 'Processing') displayStatus = 'Processing';
        else if (row.status === 'completed') displayStatus = 'Completed';
        else if (row.status === 'rejected') displayStatus = 'Rejected';
        else displayStatus = row.status || 'Pending';
        
        console.log(`Order ${row.order_id}: DB status="${row.status}", has_pdf=${!!row.submitted_pdf_url}, display_status="${displayStatus}"`);
        
        return {
          id: row.order_id,
          customer_name: row.customer_name,
          customer_email: row.customer_email || '',
          customer_phone: row.customer_phone || '',
          customer_address: row.customer_address || '',
          service_name: row.service_type,
          submission_date: row.created_at,
          status: displayStatus,
          payment_amount: Number(row.application_fee || 0) + Number(row.service_fee || 0),
          payment_date: row.created_at,
          transaction_id: row.transaction_id || '-',
          uploaded_documents: Object.keys(row.documents || {}).map((name) => ({ name, url: '#', type: 'file' })),
          submitted_pdf_url: row.submitted_pdf_url,
          submitted_at: row.submitted_at || row.updated_at,
          admin_verified_at: row.verified_at,
          isNewSubmission: isNewSubmission,
          service_details: {
            application_fee: row.application_fee || 0,
            service_fee: row.service_fee || 0,
            processing_time: '2-4 hours',
            requirements: Object.keys(row.documents || {})
          }
        };
      });
      
      console.log('Built applications:', applicationsBuilt);
      setApplications(applicationsBuilt);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
      // Set empty array to prevent UI issues
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortApplications = () => {
    let filtered = [...applications];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app =>
        app.id.toLowerCase().includes(query) ||
        app.customer_name.toLowerCase().includes(query) ||
        app.service_name.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'submission_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredApplications(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = (application) => {
    console.log('handleViewDetails called');
    console.log('Application data:', application);
    console.log('Current showDetailsModal:', showDetailsModal);
    setSelectedApplication(application);
    setShowDetailsModal(true);
    console.log('Modal state updated - should show now');
  };

  const handleApprove = async (applicationId) => {
    try {
      console.log('Approving application:', applicationId);
      
      // First, check if the application exists in available_orders
      const { data: availOrder, error: checkError } = await supabase
        .from('available_orders_a7b5c9')
        .select('*')
        .eq('order_id', applicationId)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking available order:', checkError);
        throw new Error(`Database error: ${checkError.message}`);
      }
      
      if (!availOrder) {
        throw new Error('Application not found in available orders');
      }
      
      console.log('Found available order:', availOrder);
      
      // Update available_orders status to completed
      const { error: availError } = await supabase
        .from('available_orders_a7b5c9')
        .update({ 
          status: 'completed',
          verified_at: new Date().toISOString() 
        })
        .eq('order_id', applicationId);
      
      if (availError) {
        console.error('Error updating available order:', availError);
        throw new Error(`Failed to update available order: ${availError.message}`);
      }
      
      console.log('Updated available order successfully');

      // Update operator_orders status to completed (if exists)
      const { data: operatorOrder, error: operatorCheckError } = await supabase
        .from('operator_orders_a7b5c9')
        .select('*')
        .eq('order_id', applicationId)
        .maybeSingle();
      
      if (operatorCheckError && operatorCheckError.code !== 'PGRST116') {
        console.error('Error checking operator order:', operatorCheckError);
        // Don't throw error here, continue with available order update
      }
      
      if (operatorOrder) {
        const { error: operatorError } = await supabase
          .from('operator_orders_a7b5c9')
          .update({ 
            status: 'completed'
          })
          .eq('order_id', applicationId);
        
        if (operatorError) {
          console.error('Error updating operator order:', operatorError);
          // Don't throw error here, the main update succeeded
        } else {
          console.log('Updated operator order successfully');
        }
      }

      // Update local state (map to Completed for admin UI)
      setApplications(prev => prev.map(app =>
        app.id === applicationId ? { ...app, status: 'Completed' } : app
      ));

      // Send notifications (non-blocking)
      try {
        await sendNotifications(applicationId, 'approved');
      } catch (notifError) {
        console.warn('Failed to send notifications:', notifError);
        // Don't block the approval process for notification failures
      }
      
      toast.success('Application approved successfully');
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error approving application:', error);
      toast.error(`Failed to approve application: ${error.message || 'Unknown error'}`);
    }
  };

  const handleReject = async (applicationId) => {
    if (!window.confirm('Are you sure you want to send this application back to the operator for revision?')) {
      return;
    }
    
    try {
      console.log('Rejecting application (sending back to operator):', applicationId);
      
      // Update available_orders status back to 'assigned' (so operator can revise)
      const { error: availError } = await supabase
        .from('available_orders_a7b5c9')
        .update({ 
          status: 'assigned',
          verified_at: new Date().toISOString()
        })
        .eq('order_id', applicationId);
      
      if (availError) {
        console.error('Error updating available order:', availError);
        throw new Error(`Failed to update available order: ${availError.message}`);
      }

      // Update operator_orders status back to 'assigned' (if exists)
      const { data: operatorOrder, error: operatorCheckError } = await supabase
        .from('operator_orders_a7b5c9')
        .select('*')
        .eq('order_id', applicationId)
        .maybeSingle();
      
      if (operatorOrder) {
        const { error: operatorError } = await supabase
          .from('operator_orders_a7b5c9')
          .update({ 
            status: 'assigned'
          })
          .eq('order_id', applicationId);
        
        if (operatorError) {
          console.error('Error updating operator order:', operatorError);
          // Don't throw error here, the main update succeeded
        }
      }

      setApplications(prev => prev.map(app =>
        app.id === applicationId ? { ...app, status: 'Filed' } : app
      ));

      // Send notifications (non-blocking)
      try {
        await sendNotifications(applicationId, 'rejected');
      } catch (notifError) {
        console.warn('Failed to send notifications:', notifError);
      }
      
      toast.success('Application sent back to operator for revision');
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error(`Failed to reject application: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDelete = async (applicationId) => {
    if (!window.confirm('Are you sure you want to permanently delete this application? This action cannot be undone and will remove all data from the database.')) {
      return;
    }
    
    try {
      console.log('Deleting application:', applicationId);
      
      // Delete from operator_orders_a7b5c9 table (if exists)
      const { error: operatorError } = await supabase
        .from('operator_orders_a7b5c9')
        .delete()
        .eq('order_id', applicationId);
      
      if (operatorError && operatorError.code !== 'PGRST116') {
        console.error('Error deleting from operator_orders:', operatorError);
        // Don't throw error here, continue with available order deletion
      }
      
      // Delete from available_orders_a7b5c9 table
      const { error: availableError } = await supabase
        .from('available_orders_a7b5c9')
        .delete()
        .eq('order_id', applicationId);
      
      if (availableError) {
        console.error('Error deleting from available_orders:', availableError);
        throw new Error(`Failed to delete application: ${availableError.message}`);
      }
      
      // Remove from local state
      setApplications(prev => prev.filter(app => app.id !== applicationId));
      
      toast.success('Application deleted permanently');
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error(`Failed to delete application: ${error.message || 'Unknown error'}`);
    }
  };

  const sendNotifications = async (applicationId, action) => {
    const application = applications.find(app => app.id === applicationId);
    if (!application) return;

    try {
      // Simulate sending notifications
      console.log(`Sending ${action} notification to ${application.customer_email}`);
      console.log(`Sending WhatsApp notification to ${application.customer_phone}`);
      
      // In a real app, you would integrate with email and WhatsApp APIs
      // await sendEmail(application.customer_email, action);
      // await sendWhatsApp(application.customer_phone, action);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return true;
    } catch (error) {
      console.error('Error sending notifications:', error);
      throw error;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Filed': return 'bg-blue-100 text-blue-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Processing': return 'bg-purple-100 text-purple-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Verify Applications</h1>
              <p className="text-gray-600 mt-1">
                Review and manage customer applications
              </p>
            </div>
            <Button
              variant="outline"
              onClick={fetchApplications}
              className="flex items-center"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by ID, name, or service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="Filed">Filed</option>
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Completed">Completed</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <div>
                <p className="text-sm text-gray-600">
                  Showing {filteredApplications.length} of {applications.length} applications
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Status breakdown: 
                  {' '}Pending: {applications.filter(a => a.status === 'Pending').length}
                  {' | '}Filed: {applications.filter(a => a.status === 'Filed').length}
                  {' | '}Processing: {applications.filter(a => a.status === 'Processing').length}
                  {' | '}Completed: {applications.filter(a => a.status === 'Completed').length}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Applications Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('id')}
                      >
                        <div className="flex items-center">
                          Application ID
                          {sortField === 'id' && (
                            <SafeIcon 
                              icon={sortDirection === 'asc' ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} 
                              className="ml-1 w-4 h-4" 
                            />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('customer_name')}
                      >
                        <div className="flex items-center">
                          Customer Name
                          {sortField === 'customer_name' && (
                            <SafeIcon 
                              icon={sortDirection === 'asc' ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} 
                              className="ml-1 w-4 h-4" 
                            />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service Name
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('submission_date')}
                      >
                        <div className="flex items-center">
                          Submission Date & Time
                          {sortField === 'submission_date' && (
                            <SafeIcon 
                              icon={sortDirection === 'asc' ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} 
                              className="ml-1 w-4 h-4" 
                            />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          {sortField === 'status' && (
                            <SafeIcon 
                              icon={sortDirection === 'asc' ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} 
                              className="ml-1 w-4 h-4" 
                            />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentApplications.map((application) => (
                      <tr 
                        key={application.id} 
                        className={`hover:bg-gray-50 ${
                          application.isNewSubmission ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center">
                            <span className="font-mono">{formatOrderId(application.id, { date: application.submission_date })}</span>
                            {application.isNewSubmission && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                New
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {application.customer_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {application.service_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(application.submission_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(application.status)}`}>
                              {application.status}
                            </span>
                            {application.submitted_pdf_url && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title="PDF Submitted">
                                <SafeIcon icon={FiFileText} className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex flex-wrap gap-1.5">
                            {/* View PDF Button - Show prominently if PDF exists */}
                            {application.submitted_pdf_url && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(application.submitted_pdf_url, '_blank');
                                }}
                                className="flex items-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                              >
                                <SafeIcon icon={FiFileText} className="w-3.5 h-3.5 mr-1" />
                                PDF
                              </Button>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('Details clicked for:', application.id);
                                handleViewDetails(application);
                              }}
                              className="flex items-center border-gray-300 hover:bg-gray-50"
                            >
                              <SafeIcon icon={FiEye} className="w-3.5 h-3.5 mr-1" />
                              Details
                            </Button>
                            
                            {(application.status === 'Filed' || application.status === 'Pending' || application.status === 'Processing') && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(application.id);
                                  }}
                                  className="flex items-center bg-green-600 hover:bg-green-700 text-white shadow-sm"
                                  disabled={loading}
                                >
                                  <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReject(application.id);
                                  }}
                                  className="flex items-center bg-red-600 hover:bg-red-700 text-white shadow-sm"
                                  disabled={loading}
                                >
                                  <SafeIcon icon={FiX} className="w-3.5 h-3.5 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(application.id);
                              }}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Delete Application"
                              disabled={loading}
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                            {application.status === 'Completed' && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
                                Verified
                              </span>
                            )}
                            {application.status === 'Rejected' && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                <SafeIcon icon={FiX} className="w-3 h-3 mr-1" />
                                Rejected
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastItem, filteredApplications.length)}
                        </span>{' '}
                        of <span className="font-medium">{filteredApplications.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="rounded-l-md"
                        >
                          Previous
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            onClick={() => setCurrentPage(page)}
                            className="rounded-none"
                          >
                            {page}
                          </Button>
                        ))}
                        <Button
                          variant="outline"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="rounded-r-md"
                        >
                          Next
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Application Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowDetailsModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 border-b">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Application Details
                  </h3>
                  <p className="text-primary-100 text-sm mt-0.5">Order ID: <span className="font-mono">{formatOrderId(selectedApplication.id, { date: selectedApplication.submission_date })}</span></p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer Details */}
                <Card className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                    Customer Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Name:</span> {selectedApplication.customer_name}
                    </div>
                    <div className="flex items-center">
                      <SafeIcon icon={FiMail} className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedApplication.customer_email}
                    </div>
                    <div className="flex items-center">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedApplication.customer_phone}
                    </div>
                    <div className="flex items-start">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                      {selectedApplication.customer_address}
                    </div>
                  </div>
                </Card>

                {/* Payment Information */}
                <Card className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 mr-2" />
                    Payment Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Amount:</span> ₹{selectedApplication.payment_amount}
                    </div>
                    <div>
                      <span className="font-medium">Date:</span> {formatDate(selectedApplication.payment_date)}
                    </div>
                    <div>
                      <span className="font-medium">Transaction ID:</span> {selectedApplication.transaction_id}
                    </div>
                  </div>
                </Card>

                {/* Service Details */}
                <Card className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
                    Service Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Service:</span> {selectedApplication.service_name}
                    </div>
                    <div>
                      <span className="font-medium">Application Fee:</span> ₹{selectedApplication.service_details.application_fee}
                    </div>
                    <div>
                      <span className="font-medium">Service Fee:</span> ₹{selectedApplication.service_details.service_fee}
                    </div>
                    <div className="flex items-center">
                      <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedApplication.service_details.processing_time}
                    </div>
                  </div>
                </Card>

                {/* Submitted Application PDF */}
                {selectedApplication.submitted_pdf_url && (
                  <Card className="p-4 lg:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
                      Submitted Application PDF
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-green-900">Application PDF Submitted</p>
                            {selectedApplication.submitted_at && (
                              <p className="text-xs text-green-700 mt-0.5">
                                Submitted: {formatDate(selectedApplication.submitted_at)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(selectedApplication.submitted_pdf_url, '_blank')}
                          >
                            <SafeIcon icon={FiEye} className="w-4 h-4 mr-1" />
                            View PDF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = selectedApplication.submitted_pdf_url;
                              link.download = `application-${selectedApplication.id}.pdf`;
                              link.click();
                            }}
                          >
                            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Uploaded Documents */}
                <Card className="p-4 lg:col-span-2">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                    Customer Documents
                  </h4>
                  <div className="space-y-2">
                    {selectedApplication.uploaded_documents.length > 0 ? (
                      selectedApplication.uploaded_documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{doc.name}</span>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(doc.url, '_blank')}
                          >
                            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-2">No documents uploaded</p>
                    )}
                  </div>
                </Card>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              {(selectedApplication.status === 'Filed' || selectedApplication.status === 'Pending' || selectedApplication.status === 'Processing') && (
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowDetailsModal(false)}
                    className="border-gray-300"
                  >
                    Close
                  </Button>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => handleReject(selectedApplication.id)}
                      className="bg-red-600 hover:bg-red-700 text-white shadow-sm"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                      Reject Application
                    </Button>
                    <Button
                      onClick={() => handleApprove(selectedApplication.id)}
                      className="bg-green-600 hover:bg-green-700 text-white shadow-sm"
                      disabled={loading}
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                      Approve Application
                    </Button>
                  </div>
                </div>
              )}
              {(selectedApplication.status === 'Completed' || selectedApplication.status === 'Rejected') && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowDetailsModal(false)}
                    className="border-gray-300"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default VerifyApplications;
